import {DialogBody, DialogDismiss, DialogFooter} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Text} from '@/components/ui/text/Text'
import {useTranslation} from '@/translations/i18n'
import {ScaleType} from '../../types'
import {iadlScaleQuestions, scaleTypeToLabel} from '../../utils'
import {StyledDialog, StyledDialogHeader, StyledTotalScore} from './style'
import {Button} from '@/components/ui/button/Button'
import {zodResolver} from '@hookform/resolvers/zod'
import {Controller, useForm} from 'react-hook-form'
import {StoreIadlEvaluationScaleFormSchema} from '../../formSchemas'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {SelectProvider} from '@ariakit/react'
import {SelectItem, SelectList, SelectPopover, SelectTrigger} from '@/components/ui/select-atoms/SelectAtoms'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Badge} from '@/components/ui/badge/Badge'
import {ObjectEntries} from '@/utilities/helpers'

export const IadlScaleDialog = ({
    defaultValues,
    evaluationScaleCount,
    onConfirm
}: {
    defaultValues?: StoreIadlEvaluationScaleFormSchema
    evaluationScaleCount: number
    onConfirm: (values: StoreIadlEvaluationScaleFormSchema, totalScore: number) => void
}) => {
    const {t} = useTranslation()
    const {
        control,
        handleSubmit,
        formState: {isValid},
        watch
    } = useForm<StoreIadlEvaluationScaleFormSchema>({
        resolver: zodResolver(StoreIadlEvaluationScaleFormSchema),
        defaultValues: {
            questions: defaultValues?.questions,
            type: ScaleType.enum.lawton_iadl
        }
    })

    const totalScore = ObjectEntries(watch('questions') ?? {}).reduce((sum: number, [key, value]) => {
        const question = iadlScaleQuestions.find(question => question.name == key)
        if (question) {
            const option = question.options.find(option => option.value == value)
            return sum + (option ? option.score : 0)
        }
        return sum
    }, 0)

    const onSubmit = handleSubmit(values => onConfirm(values, totalScore))

    return (
        <StyledDialog width="800px">
            <StyledDialogHeader>
                <Text render="h1" size="textMd" weight="500">
                    {t('projects:x_evaluation_scale', {count: evaluationScaleCount})}
                </Text>
                <Text render="h1" size="textMd" weight="500">
                    {t(scaleTypeToLabel[ScaleType.enum.lawton_iadl])}
                </Text>
            </StyledDialogHeader>
            <DialogBody>
                {iadlScaleQuestions.map((question, index) => (
                    <Flexbox key={index} direction="column" gap={1.5}>
                        <Text render="h2" size="textSm" weight="500">
                            {`${index + 1}. ${t(question.title)}`}
                        </Text>
                        <Controller
                            control={control}
                            name={`questions.${question.name}`}
                            render={({field, fieldState}) => (
                                <SelectProvider defaultValue="" value={field.value} setValue={field.onChange}>
                                    <SelectTrigger
                                        fullWidth
                                        placeholder={t('commons:select')}
                                        displayValue={value => {
                                            const label = question.options.find(option => option.value == value)?.label
                                            return label ? t(label) : value
                                        }}
                                    />
                                    {fieldState.error && <InputHelpText error={t('errors:required')} />}
                                    <SelectPopover>
                                        <SelectList>
                                            {question.options.map(option => (
                                                <SelectItem key={option.value} value={option.value}>
                                                    {t(option.label)}{' '}
                                                    <Badge size="sm">
                                                        {t('commons:score')}: {option.score}
                                                    </Badge>
                                                </SelectItem>
                                            ))}
                                        </SelectList>
                                    </SelectPopover>
                                </SelectProvider>
                            )}
                        />
                    </Flexbox>
                ))}
                <StyledTotalScore>
                    {t('commons:total_score')} {totalScore}
                </StyledTotalScore>
            </DialogBody>
            <DialogFooter>
                <DialogDismiss render={<Button variant="secondary" />}>{t('commons:close')}</DialogDismiss>
                <DialogDismiss
                    onClick={event => {
                        if (!isValid) {
                            event.preventDefault()
                        }
                        onSubmit()
                    }}
                    render={<Button variant="primaryNeutral" />}
                >
                    {t('commons:confirm')}
                </DialogDismiss>
            </DialogFooter>
        </StyledDialog>
    )
}
